import { settings } from '@rhim/design';
import { XMiniIcon } from '@rhim/icons/16/XMiniIcon';
import { isDefined } from '@rhim/utils';
import { notification } from 'antd';
import { ArgsProps } from 'antd/es/notification/interface';
import React from 'react';

import SvgUnhealthy from '../../../../../../assets/icons-24/app-state-unhealthy-24.svg';
import SvgInfo from '../../../../../../assets/icons-24/info-24.svg';
import SvgProgress from '../../../../../../assets/icons-24/loader-24.svg';
import SvgSuccess from '../../../../../../assets/icons-24/valid-24.svg';
import SvgAlert from '../../../../../../assets/icons-24/warning-24.svg';
import { Button } from '../Button/Button';
import { NotificationActions } from './Actions';
import type { SystemNotificationType } from './types';
import { calculateDuration } from './utils';

export const Notification = {
  open: (options: SystemNotificationType) => {
    const { key, type, message, description, action, onCloseCallback, duration, getContainer, closeIcon, userAcknowledgementButtonLabel } = options;

    const config: ArgsProps = {
      title: message,
      description,
      closeIcon: isDefined(closeIcon) ? closeIcon : <XMiniIcon />,
      key,
      ...(onCloseCallback && { onClose: () => onCloseCallback(key) }),
    };

    // Render action buttons/links
    if (isDefined(action)) {
      config.actions = <NotificationActions action={action} onActionComplete={() => notification.destroy(key)} />;
    } else if (isDefined(userAcknowledgementButtonLabel)) {
      // Legacy support for userAcknowledgementButtonLabel
      config.actions = (
        <Button
          label={userAcknowledgementButtonLabel}
          onClick={() => {
            notification.destroy(key);
            if (onCloseCallback) {
              onCloseCallback(key);
            }
          }}
          size="x-small-32"
          autoFocus
        />
      );
    }

    const hasAction = isDefined(action) || isDefined(userAcknowledgementButtonLabel);
    const autoHideEnabled = !(hasAction || type === 'progress' || duration === 0);
    config.duration = autoHideEnabled ? calculateDuration(duration, message, description) : 0;

    let icon;
    switch (type) {
      case 'info':
        icon = <SvgInfo />;
        break;
      case 'success':
        icon = <SvgSuccess />;
        break;
      case 'alert':
        icon = <SvgAlert />;
        break;
      case 'warning':
      case 'warning-emphasis':
        icon = <SvgUnhealthy />;
        break;
      case 'progress':
        icon = <SvgProgress />;
        break;
      default:
        throw new Error(`Unexpected type : '${type}'`);
    }
    notification.config({
      placement: 'bottomRight',
      getContainer,
    });
    notification.info({
      ...config,
      className: type,
      icon,
      description: (
        <React.Fragment>
          <StyleSheet />
          {description}
        </React.Fragment>
      ),
    });
  },
  destroy: (key?: string) => notification.destroy(key),
  config: notification.config,
};

const StyleSheet = settings.globals.patchedCreateGlobalStyle`
  .ant-notification {
    margin-right: 4px;
  }

  .ant-notification .ant-notification-notice-wrapper {
    box-shadow: 0px 2px 16px 0px rgba(0, 0, 0, 0.05);
  }

  .ant-notification .ant-notification-notice-wrapper .ant-notification-notice {
    --border-radius: 2px;

    width: 480px;
    padding: ${settings.Spacing.Spacing_200};
    padding-left: ${settings.Spacing.Spacing_250};
    border: 1px solid ${settings.colors.Primary.Grey_2};
    border-radius: var(--border-radius);
    border-left: none;

    --marginLeft: calc(${settings.Spacing.Spacing_300} + ${settings.Spacing.Spacing_200}); /* icon width(24px) + 16px */
    --textColor: ${settings.colors.Primary.Grey_8};

    &.info {
      --leftSideColor: ${settings.colors.Primary.Grey_5};

      background-color: ${settings.colors.Monochromatic.White};
    }

    &.success {
      --leftSideColor: ${settings.colors.Operational.State_Notif_Green_2};

      background-color: ${settings.colors.Monochromatic.White};
    }

    &.alert {
      --leftSideColor: ${settings.colors.Operational.State_Notif_Magenta_2};
      --textColor: ${settings.colors.Operational.State_Notif_Magenta_4};

      background-color: ${settings.colors.Operational.State_Notif_Magenta_1};
      border-color: ${settings.colors.Operational.State_Notif_Magenta_1};
    }

    &.warning {
      --leftSideColor: ${settings.colors.Operational.State_Notif_Yellow_2};

      background-color: ${settings.colors.Monochromatic.White};
    }

    &.warning-emphasis {
      --leftSideColor: ${settings.colors.Operational.State_Notif_Yellow_2};

      background-color: ${settings.colors.Operational.State_Notif_Yellow_1};
      border-color: ${settings.colors.Operational.State_Notif_Yellow_2};
    }

    &.progress {
      --leftSideColor: ${settings.colors.Operational.State_Blue_2};

      background-color: ${settings.colors.Monochromatic.White};

      svg {
        animation: rotating 2s linear infinite;
      }

      @keyframes rotating {
        from {
          transform: rotate(0deg);
        }

        to {
          transform: rotate(360deg);
        }
      }

      .ant-notification-notice-close {
        display: none;
      }
    }

    &::before {
      content: '';
      width: 4px;
      height: 100%;
      background-color: var(--leftSideColor);
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      border-top-left-radius: var(--border-radius);
      border-bottom-left-radius: var(--border-radius);
    }

    .ant-notification-notice-with-icon {
      .ant-notification-notice-title {
        font-size: ${settings.typography.FontSize.Medium};
        font-family: ${settings.typography.FontFamily.Bold};
        line-height: ${settings.typography.LineHeight.Line_Height_20};
        margin-left: var(--marginLeft);
        margin-bottom: 0;
        color: var(--textColor);

        &:not(:empty) {
          padding-block: 2px;
          
          & + .ant-notification-notice-description:not(:empty) {
            margin-top: ${settings.Spacing.Spacing_100};
          }
        }
      }

      .ant-notification-notice-description {
        font-size: ${settings.typography.FontSize.Small};
        font-family: ${settings.typography.FontFamily.Regular};
        line-height: ${settings.typography.LineHeight.Line_Height_20};
        margin-top: 0;
        margin-inline: var(--marginLeft);
        color: var(--textColor);
      }

      .ant-notification-notice-icon {
        margin: 0;
      }
    }

    &.ant-notification-notice .ant-notification-notice-close {
      top: ${settings.Spacing.Spacing_200};
      inset-inline-end: ${settings.Spacing.Spacing_200};
    }

    .ant-notification-notice-actions {
      margin-top: ${settings.Spacing.Spacing_100};
      margin-inline-start: var(--marginLeft);
      float: left;
    }
  }
`;
